import { QueryStatus } from "@reduxjs/toolkit/dist/query"
import { useCallback, useRef } from "react"
import {
  useLazyValidateNMLSQuery,
  useLazyValidateEmailQuery,
  useLazyFetchSubscriptionDetailsQuery
} from "../../redux/queries"
import {
  CreateSubscriptionPayload,
  LoanOfficer,
  SubscriptionDetails
} from "../../types"

export const useValidateEmailAndNMLS = () => {
  const [lazyValidateNMLS] = useLazyValidateNMLSQuery()
  const [lazyValidateEmail] = useLazyValidateEmailQuery()
  const [lazyFetchSubscriptionDetailsQuery] =
    useLazyFetchSubscriptionDetailsQuery()

  // Track the last validated email and brand_id to prevent unnecessary API calls
  const lastValidatedEmailRef = useRef<string>("")
  const lastValidatedBrandIdRef = useRef<string>("")
  const lastSubscriptionErrorsRef = useRef<Partial<CreateSubscriptionPayload>>(
    {}
  )
  // Timeout ref for debouncing email validation API calls
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const emailValidationTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Track the last validated NMLS and email for LO validation
  const lastValidatedNMLSRef = useRef<string>("")
  const lastValidatedLOEmailRef = useRef<string>("")
  const lastValidatedExcludeIdRef = useRef<string | undefined>("")
  const lastLOErrorsRef = useRef<Partial<LoanOfficer>>({})

  const validateLO = useCallback(
    async (values: LoanOfficer) => {
      const excludeId = values.uid || values.loid
      const isNotActiveError =
        "This profile is created but inactive. Email support@bankingbridge.com to activate the account."

      const nmlsValue = values.nmls?.trim() || ""
      const emailValue = values.email?.trim() || ""

      // Check if values have changed
      const nmlsChanged =
        nmlsValue !== lastValidatedNMLSRef.current ||
        excludeId !== lastValidatedExcludeIdRef.current
      const emailChanged =
        emailValue !== lastValidatedLOEmailRef.current ||
        excludeId !== lastValidatedExcludeIdRef.current

      // If nothing has changed, return cached errors
      if (!nmlsChanged && !emailChanged) {
        return lastLOErrorsRef.current
      }

      // Start with cached errors to preserve previous validations
      const errors: Partial<LoanOfficer> = { ...lastLOErrorsRef.current }

      // Only validate NMLS if it has a meaningful value and has changed
      if (nmlsValue.length > 0 && nmlsChanged) {
        const { data, status } = await lazyValidateNMLS({
          nmls: nmlsValue,
          excludeId
        })

        // Update ref with current NMLS value
        lastValidatedNMLSRef.current = nmlsValue
        lastValidatedExcludeIdRef.current = excludeId

        if (status === QueryStatus.fulfilled && data.conflictingLo) {
          if (!data.conflictingLo?.active) errors.nmls = isNotActiveError
          else if (
            data.conflictingLo.loid !== values.uid &&
            data.conflictingLo.loid !== values.loid
          )
            errors.nmls = `This NMLS is already in use by ${data.conflictingLo.name} (${data.conflictingLo.bank})`
        } else {
          // Clear NMLS error if validation passed
          delete errors.nmls
        }
      } else if (nmlsValue.length === 0) {
        // Clear NMLS error if field is empty
        delete errors.nmls
      }

      // Only validate email if it has a meaningful value and has changed
      if (emailValue.length > 0 && emailChanged) {
        const { data, status } = await lazyValidateEmail({
          email: emailValue,
          excludeId
        })

        // Update ref with current email value
        lastValidatedLOEmailRef.current = emailValue
        lastValidatedExcludeIdRef.current = excludeId

        if (status === QueryStatus.fulfilled && data.conflictingLo) {
          if (!data.conflictingLo?.active) errors.email = isNotActiveError
          else if (
            data.conflictingLo.uid !== values.uid &&
            data.conflictingLo.uid !== values.loid
          )
            errors.email = `This email is already in use by ${data.conflictingLo.name} (${data.conflictingLo.bank})`
        } else {
          // Clear email error if validation passed
          delete errors.email
        }
      } else if (emailValue.length === 0) {
        // Clear email error if field is empty
        delete errors.email
      }

      // Cache the accumulated errors
      lastLOErrorsRef.current = errors
      return errors
    },
    [lazyValidateNMLS, lazyValidateEmail]
  )

  const validateSubscriptionEmail = useCallback(
    async (
      values: CreateSubscriptionPayload | SubscriptionDetails,
      brand_id: string
    ) => {
      const emailValue = values.email.trim()

      // Check if values have changed
      const valuesChanged =
        emailValue !== lastValidatedEmailRef.current ||
        brand_id !== lastValidatedBrandIdRef.current

      // If nothing has changed, return cached errors
      if (!valuesChanged) {
        return lastSubscriptionErrorsRef.current
      }

      // Clear any pending validation timeout
      if (emailValidationTimeoutRef.current) {
        clearTimeout(emailValidationTimeoutRef.current)
      }

      // Return a promise that resolves after debounce delay
      return new Promise<Partial<CreateSubscriptionPayload>>(resolve => {
        emailValidationTimeoutRef.current = setTimeout(async () => {
          const errors: Partial<CreateSubscriptionPayload> = {}

          // Only fetch if email or brand_id has changed
          if (emailValue) {
            const { data, status } = await lazyFetchSubscriptionDetailsQuery({
              email: emailValue,
              brand_id
            })

            // Update refs with current values
            lastValidatedEmailRef.current = emailValue
            lastValidatedBrandIdRef.current = brand_id

            if (
              status === QueryStatus.fulfilled &&
              data?.lead_id &&
              data?.lead_id !== values.lead_id
            ) {
              errors.email = `This email is already in use by ${data.first_name} ${data.last_name}`
              // Cache and return errors
              lastSubscriptionErrorsRef.current = errors
              resolve(errors)
              return
            }
          }

          // Cache the successful validation (no errors)
          lastSubscriptionErrorsRef.current = errors
          resolve(errors)
        }, 500) // 500ms debounce delay
      })
    },
    [lazyFetchSubscriptionDetailsQuery]
  )

  return { validateLO, validateSubscriptionEmail }
}
